import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  Dropdown,
  Input,
  Modal,
  Pagination,
  Space,
  Table,
  Tooltip,
  Typography,
  message
} from 'antd'
import { useMsal } from '@azure/msal-react'
import Button from '../../components/common/button/button'
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExportOutlined,
  FolderOpenOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons'
import AddCategoryDrawer from './addCategoryDrawer/addCategoryDrawer'
import {
  buildCategoryUrl,
  buildProductUrl,
  countAllCategories,
  findCategoryNode,
  flattenCategoriesForSelect,
  mapCategoriesToRoot,
  removeCategoryNode
} from './contentManagementUtils'
import { assignProductsToCategory, createCategory, deleteCategory, fetchCategoryList, fetchCategoryProducts, removeProductFromCategory, updateCategory } from '../../api/categoryApiService/categoryApiUtils'
import './contentManagement.scss'

const { Title, Text } = Typography

const ContentManagement = () => {
  const { instance: msalInstance } = useMsal()
  const [categories, setCategories] = useState([])
  const [categoryProducts, setCategoryProducts] = useState({})
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [expandedKeys, setExpandedKeys] = useState([])
  const [searchExpandedKeys, setSearchExpandedKeys] = useState([])

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState('add-root')
  const [drawerTitle, setDrawerTitle] = useState('Add Category')
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(null)
  const [productsModalOpen, setProductsModalOpen] = useState(false)
  const [activeProductCategory, setActiveProductCategory] = useState(null)
  const [productInput, setProductInput] = useState('')
  const [productsCurrentPage, setProductsCurrentPage] = useState(1)
  const [productsPageSize, setProductsPageSize] = useState(10)
  const [selectedProductCodes, setSelectedProductCodes] = useState([])
  const [productDescriptions, setProductDescriptions] = useState({})
  const [productsLoading, setProductsLoading] = useState(false)
  const [deletingSelectedProducts, setDeletingSelectedProducts] = useState(false)

  const loadCategories = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchCategoryList(msalInstance)
      setCategories(data)
    } catch {
      message.error('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }, [msalInstance])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const filteredCategories = useMemo(() => {
    const sortTreeByOrder = (nodes = []) => {
      return [...nodes]
        .sort((a, b) => {
          const aOrder = Number.isFinite(a.sortOrder) ? a.sortOrder : Number.MAX_SAFE_INTEGER
          const bOrder = Number.isFinite(b.sortOrder) ? b.sortOrder : Number.MAX_SAFE_INTEGER

          if (aOrder !== bOrder) {
            return aOrder - bOrder
          }

          return (a.name || '').localeCompare(b.name || '')
        })
        .map((node) => ({
          ...node,
          children: sortTreeByOrder(node.children || [])
        }))
    }

    const sortedCategories = sortTreeByOrder(categories)

    if (!searchText.trim()) {
      return sortedCategories
    }

    const query = searchText.toLowerCase()

    const filterTree = (nodes) => {
      return nodes
        .map((node) => {
          const childMatches = filterTree(node.children || [])
          const nodeMatches = node.name.toLowerCase().includes(query)

          if (nodeMatches || childMatches.length) {
            return {
              ...node,
              children: childMatches
            }
          }

          return null
        })
        .filter(Boolean)
    }

    return filterTree(sortedCategories)
  }, [categories, searchText])

  const totalCategories = useMemo(() => countAllCategories(categories), [categories])

  const rootCategoryByKey = useMemo(() => mapCategoriesToRoot(categories), [categories])

  const expandableCategoryKeys = useMemo(() => {
    const keys = []
    const walk = (nodes = []) => {
      nodes.forEach((node) => {
        if (node.children?.length) {
          keys.push(node.key)
          walk(node.children)
        }
      })
    }
    walk(filteredCategories)
    return keys
  }, [filteredCategories])

  useEffect(() => {
    setSearchExpandedKeys(searchText.trim() ? expandableCategoryKeys : [])
  }, [searchText, expandableCategoryKeys])

  const visibleExpandedKeys = searchText.trim() ? searchExpandedKeys : expandedKeys
  const isAllExpanded = expandableCategoryKeys.length > 0 &&
    expandableCategoryKeys.every((key) => visibleExpandedKeys.includes(key))

  const toggleExpandAll = () => {
    const setVisibleKeys = searchText.trim() ? setSearchExpandedKeys : setExpandedKeys
    setVisibleKeys(isAllExpanded ? [] : expandableCategoryKeys)
  }

  const pagedTopLevelCategories = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredCategories.slice(start, start + pageSize)
  }, [filteredCategories, currentPage, pageSize])

  const parentCategoryOptions = useMemo(() => {
    return flattenCategoriesForSelect(categories)
  }, [categories])

  const openDrawer = (mode, categoryKey = null) => {
    if (mode === 'add-child' && categoryKey && getOwnProductCount(categoryKey) > 0) {
      message.warning('Cannot add subcategory when this category already has products.')
      return
    }

    setDrawerMode(mode)
    setSelectedCategoryKey(categoryKey)

    if (mode === 'add-root') {
      setDrawerTitle('Add Category')
    } else if (mode === 'add-child') {
      setDrawerTitle('Add Subcategory')
    } else {
      const category = categoryKey ? findCategoryNode(categories, categoryKey) : null
      setDrawerTitle(category?.parentId ? 'Edit Subcategory' : 'Edit Category')
    }

    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
  }

  const isLeafCategory = (category) => !(category.children?.length)

  const collectCategoryKeys = (category) => {
    const keys = [category.key]

    const walkChildren = (children = []) => {
      children.forEach((child) => {
        keys.push(child.key)
        if (child.children?.length) {
          walkChildren(child.children)
        }
      })
    }

    walkChildren(category.children)
    return keys
  }

  const parseProductCodes = (rawInput) => {
    return Array.from(
      new Set(
        rawInput
          .split(/[\s,]+/)
          .map((code) => code.trim())
          .filter(Boolean)
      )
    )
  }

  const getOwnProductCount = (categoryKey) => {
    if (Object.prototype.hasOwnProperty.call(categoryProducts, categoryKey)) {
      return categoryProducts[categoryKey].length
    }

    return findCategoryNode(categories, categoryKey)?.productCount || 0
  }

  const currentCategory = useMemo(() => {
    if (!selectedCategoryKey) return null
    return findCategoryNode(categories, selectedCategoryKey)
  }, [categories, selectedCategoryKey])

  const handleSaveCategory = async (payload) => {
    if (!payload.name?.trim()) {
      message.warning('Category name is required')
      return
    }

    if (drawerMode === 'edit' && selectedCategoryKey) {
      try {
        await updateCategory(selectedCategoryKey, payload, msalInstance)
        await loadCategories()
        message.success(currentCategory?.parentId ? 'Subcategory updated' : 'Category updated')
        closeDrawer()
      } catch {
        message.error('Failed to update category')
      }
      return
    }

    const resolvedParentId = drawerMode === 'add-root'
      ? null
      : (payload.parentKey || (drawerMode === 'add-child' ? selectedCategoryKey : null))

    try {
      await createCategory({ ...payload, parentId: resolvedParentId }, msalInstance)
      await loadCategories()
      setCurrentPage(1)
      message.success(resolvedParentId ? 'Subcategory created' : 'Root category created')
      closeDrawer()
    } catch {
      message.error(resolvedParentId ? 'Failed to create subcategory' : 'Failed to create category')
    }
  }

  const handleDeleteCategory = (categoryKey) => {
    Modal.confirm({
      title: 'Delete category?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const categoryToDelete = findCategoryNode(categories, categoryKey)
        const keysToRemove = categoryToDelete ? collectCategoryKeys(categoryToDelete) : [categoryKey]

        try {
          await deleteCategory(categoryKey, msalInstance)

          setCategories((prev) => removeCategoryNode(prev, categoryKey))
          setCategoryProducts((prev) => {
            const next = { ...prev }
            keysToRemove.forEach((key) => {
              delete next[key]
            })
            return next
          })

          if (activeProductCategory && keysToRemove.includes(activeProductCategory.key)) {
            setProductsModalOpen(false)
            setActiveProductCategory(null)
            setProductInput('')
          }

          message.success('Category deleted')
        } catch {
          message.error('Failed to delete category')
        }
      }
    })
  }

  const loadCategoryProducts = async (categoryKey) => {
    const limit = 100
    const firstPage = await fetchCategoryProducts(categoryKey, msalInstance, { page: 1, limit })
    const items = [...firstPage.items]
    const totalPages = firstPage.pagination?.totalPages || Math.ceil((firstPage.pagination?.total || items.length) / limit)

    for (let page = 2; page <= totalPages; page += 1) {
      const result = await fetchCategoryProducts(categoryKey, msalInstance, { page, limit })
      items.push(...result.items)
    }

    const keyCodes = items.map((item) => item.product?.keyCode).filter(Boolean)

    setCategoryProducts((prev) => ({
      ...prev,
      [categoryKey]: keyCodes
    }))
    setProductDescriptions((prev) => {
      const next = { ...prev }
      items.forEach((item) => {
        if (item.product?.keyCode) {
          next[item.product.keyCode] = item.product.productDescription || ''
        }
      })
      return next
    })
  }

  const openProductsModal = async (category) => {
    if (!isLeafCategory(category)) {
      message.warning('Only leaf categories can have keycodes/products. Add under the last subcategory level.')
      return
    }

    setActiveProductCategory(category)
    setProductsCurrentPage(1)
    setProductInput('')
    setSelectedProductCodes([])
    setProductsModalOpen(true)

    setProductsLoading(true)
    try {
      await loadCategoryProducts(category.key)
    } catch {
      message.error('Failed to load products for this category')
    } finally {
      setProductsLoading(false)
    }
  }

  const closeProductsModal = () => {
    setProductsModalOpen(false)
    setSelectedProductCodes([])
  }

  const handleSaveProducts = async () => {
    if (!activeProductCategory) {
      return
    }

    const tokens = productInput.split(/[\s,]+/).map((code) => code.trim()).filter(Boolean)
    if (!tokens.length) {
      message.warning('Enter at least one keycode. Separate multiple keycodes with commas, spaces, or new lines.')
      return
    }

    const listCodes = (codes) => codes.join(', ')
    const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

    const textEntries = Array.from(new Set(tokens.filter((code) => !/^\d+$/.test(code))))
    if (textEntries.length) {
      message.error(
        textEntries.length === tokens.length
          ? 'Text is not allowed. Keycodes must contain numbers only.'
          : `Text is not allowed. Remove the non-numeric ${textEntries.length === 1 ? 'entry' : 'entries'}: ${listCodes(textEntries)}`
      )
      return
    }

    const parsedCodes = parseProductCodes(productInput)
    const duplicateCount = tokens.length - parsedCodes.length
    const existingCodes = categoryProducts[activeProductCategory.key] || []
    const alreadyAssigned = parsedCodes.filter((code) => existingCodes.includes(code))
    const newCodes = parsedCodes.filter((code) => !existingCodes.includes(code))

    if (!newCodes.length) {
      message.warning(
        alreadyAssigned.length === 1
          ? `Keycode ${alreadyAssigned[0]} is already assigned to this category.`
          : `All entered keycodes are already assigned to this category: ${listCodes(alreadyAssigned)}`
      )
      return
    }

    try {
      const result = await assignProductsToCategory(activeProductCategory.key, newCodes, msalInstance)
      const notFoundCodes = new Set(result?.notFoundKeyCodes || [])
      const acceptedCodes = newCodes.filter((code) => !notFoundCodes.has(code))
      const invalidCodes = newCodes.filter((code) => notFoundCodes.has(code))

      setCategoryProducts((prev) => ({
        ...prev,
        [activeProductCategory.key]: [...existingCodes, ...acceptedCodes]
      }))

      if (acceptedCodes.length) {
        // The assign response has no descriptions, so reload to pick them up.
        loadCategoryProducts(activeProductCategory.key).catch(() => {})
      }

      // Leave only the invalid keycodes in the box so the user can correct them.
      setProductInput(invalidCodes.join(', '))

      const notes = []
      if (invalidCodes.length) {
        notes.push(`${invalidCodes.length === 1 ? 'Invalid keycode' : 'Invalid keycodes'}: ${listCodes(invalidCodes)}.`)
      }
      if (alreadyAssigned.length) {
        notes.push(`Already assigned, skipped: ${listCodes(alreadyAssigned)}.`)
      }
      if (duplicateCount > 0) {
        notes.push(`${plural(duplicateCount, 'duplicate entry')} ignored.`.replace('entrys', 'entries'))
      }
      const notesText = notes.length ? ` ${notes.join(' ')}` : ''

      if (!acceptedCodes.length) {
        message.error(
          invalidCodes.length === 1
            ? `Invalid keycode: ${invalidCodes[0]}. No keycodes were added.${notesText.replace(` Invalid keycode: ${invalidCodes[0]}.`, '')}`
            : `None of the entered keycodes are valid. No keycodes were added.${notesText}`
        )
      } else if (notes.length) {
        message.warning(`${plural(acceptedCodes.length, 'keycode')} added to ${activeProductCategory.name}.${notesText}`, 6)
      } else {
        message.success(`${plural(acceptedCodes.length, 'keycode')} added to ${activeProductCategory.name}.`)
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to add keycodes. Please try again.')
    }
  }

  const handleDeleteProductCode = async (codeToDelete) => {
    if (!activeProductCategory) {
      return
    }

    try {
      await removeProductFromCategory(activeProductCategory.key, [codeToDelete], msalInstance)

      setCategoryProducts((prev) => {
        const nextCodes = (prev[activeProductCategory.key] || []).filter((code) => code !== codeToDelete)
        return {
          ...prev,
          [activeProductCategory.key]: nextCodes
        }
      })
      setSelectedProductCodes((prev) => prev.filter((code) => code !== codeToDelete))

      message.success(`Deleted ${codeToDelete}`)
    } catch {
      message.error('Failed to remove product from category')
    }
  }

  const confirmDeleteProductCode = (codeToDelete) => {
    Modal.confirm({
      title: `Delete keycode ${codeToDelete}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => handleDeleteProductCode(codeToDelete)
    })
  }

  const handleDeleteSelectedProductCodes = () => {
    if (!activeProductCategory || selectedProductCodes.length === 0) {
      return
    }

    Modal.confirm({
      title: `Delete ${selectedProductCodes.length} selected keycode(s)?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingSelectedProducts(true)
        try {
          await removeProductFromCategory(activeProductCategory.key, selectedProductCodes, msalInstance)

          const selectedCodes = new Set(selectedProductCodes)
          setCategoryProducts((prev) => ({
            ...prev,
            [activeProductCategory.key]: (prev[activeProductCategory.key] || [])
              .filter((code) => !selectedCodes.has(code))
          }))
          setSelectedProductCodes([])
          message.success(`${selectedCodes.size} keycode(s) deleted`)
        } catch {
          message.error('Failed to remove selected products from category')
        } finally {
          setDeletingSelectedProducts(false)
        }
      }
    })
  }

  const activeCategoryProducts = activeProductCategory
    ? (categoryProducts[activeProductCategory.key] || [])
    : []

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(activeCategoryProducts.length / productsPageSize))
    if (productsCurrentPage > lastPage) {
      setProductsCurrentPage(lastPage)
    }
  }, [activeCategoryProducts.length, productsCurrentPage, productsPageSize])

  const getCategoryActionItems = (row) => {
    const leafCategory = isLeafCategory(row)

    return [
      {
        key: 'manage-products',
        label: 'Add/View Products',
        disabled: !leafCategory,
        onClick: () => openProductsModal(row)
      },
      {
        type: 'divider'
      },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => handleDeleteCategory(row.key)
      }
    ]
  }

  const categoryColumns = [
    {
      title: (
        <Space size={8}>
          <Tooltip title={isAllExpanded ? 'Collapse all' : 'Expand all'}>
            <button
              type="button"
              aria-label={isAllExpanded ? 'Collapse all' : 'Expand all'}
              className={`ant-table-row-expand-icon ${isAllExpanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'}`}
              onClick={toggleExpandAll}
              disabled={expandableCategoryKeys.length === 0}
            />
          </Tooltip>
          <span>Category</span>
        </Space>
      ),
      dataIndex: 'name',
      key: 'name',
      render: (_, row) => (
        <div className="category-cell">
          <FolderOpenOutlined className="folder-icon" />
          <Tooltip title={row.name} placement="topLeft">
            <span className="category-name">{row.name}</span>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 280,
      ellipsis: { showTitle: false },
      render: (description) => (description ? (
        <Tooltip title={description} placement="topLeft">
          <span>{description}</span>
        </Tooltip>
      ) : <Text type="secondary">-</Text>)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const isActive = status === 'Active'

        return (
          <span className={`status-toggle ${isActive ? 'is-active' : 'is-inactive'}`}>
            {status}
          </span>
        )
      }
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 120
    },
    {
      title: 'URL',
      key: 'url',
      width: 80,
      render: (_, row) => (
        <Tooltip title="Open on anko.com">
          <a
            href={buildCategoryUrl(row, rootCategoryByKey[row.key])}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${row.name} on anko.com`}
          >
            <ExportOutlined />
          </a>
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, row) => {
        const hasOwnProducts = getOwnProductCount(row.key) > 0

        return (
          <Space size={10}>
            <Tooltip title={hasOwnProducts ? 'Disabled: category already has products' : 'Add subcategory'}>
              <Button
                type="text"
                icon={<PlusCircleOutlined />}
                disabled={hasOwnProducts}
                onClick={() => openDrawer('add-child', row.key)}
              />
            </Tooltip>
            <Tooltip title="Edit category">
              <Button type="text" icon={<EditOutlined />} onClick={() => openDrawer('edit', row.key)} />
            </Tooltip>
            <Dropdown
              menu={{
                items: getCategoryActionItems(row)
              }}
              trigger={['click']}
            >
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          </Space>
        )
      }
    }
  ]

  const displayStart = filteredCategories.length === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const displayEnd = Math.min(currentPage * pageSize, filteredCategories.length)

  return (
    <div className="category-management-page">
      <div className="page-header">
        <div>
          <Title level={3}>Category Management</Title>
          <Text type="secondary">Create and manage product categories and subcategories.</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer('add-root')}>
          Add Root Category
        </Button>
      </div>

      <Card className="categories-card" variant="borderless">
        <div className="card-header-row">
          <Title level={5}>All Categories</Title>
          <Input
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value)
              setCurrentPage(1)
            }}
            placeholder="Search categories..."
            prefix={<SearchOutlined />}
            className="search-input"
          />
        </div>

        <Table
          columns={categoryColumns}
          dataSource={pagedTopLevelCategories}
          rowKey="key"
          loading={loading}
          pagination={false}
          className="categories-table"
          scroll={{ x: 1000, y: 'max(200px, calc(100vh - 480px))' }}
          expandable={{
            expandedRowKeys: visibleExpandedKeys,
            expandIcon: ({ expanded, onExpand, record }) => {
              if (!record.children?.length) {
                return (
                  <span
                    aria-hidden="true"
                    className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced"
                  />
                )
              }

              return (
                <button
                  type="button"
                  aria-label={expanded ? 'Collapse row' : 'Expand row'}
                  className={`ant-table-row-expand-icon ${expanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'}`}
                  onClick={(event) => onExpand(record, event)}
                />
              )
            },
            onExpand: (expanded, record) => {
              const setVisibleKeys = searchText.trim() ? setSearchExpandedKeys : setExpandedKeys
              if (expanded) {
                setVisibleKeys((prev) => prev.includes(record.key) ? prev : [...prev, record.key])
              } else {
                setVisibleKeys((prev) => prev.filter((key) => key !== record.key))
              }
            }
          }}
        />

        <div className="card-footer-row">
          <Text type="secondary">Showing {displayStart} to {displayEnd} of {filteredCategories.length || totalCategories} categories</Text>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredCategories.length}
            onChange={(page, size) => {
              setCurrentPage(page)
              setPageSize(size)
            }}
            size="small"
            showSizeChanger={false}
          />
        </div>
      </Card>

      <AddCategoryDrawer
        open={drawerOpen}
        mode={drawerMode}
        title={drawerTitle}
        forceRoot={drawerMode === 'add-root'}
        parentKey={
          drawerMode === 'add-child'
            ? selectedCategoryKey
            : drawerMode === 'edit'
              ? currentCategory?.parentId || null
              : null
        }
        fixedParentCategoryName={drawerMode === 'add-child' ? currentCategory?.name : ''}
        hideParentSelector={drawerMode === 'add-child' || drawerMode === 'add-root'}
        initialValues={drawerMode === 'edit' ? currentCategory : null}
        parentOptions={parentCategoryOptions}
        onClose={closeDrawer}
        onSave={handleSaveCategory}
      />

      <Modal
        className="products-view-modal"
        open={productsModalOpen}
        width={760}
        footer={null}
        onCancel={closeProductsModal}
        title={activeProductCategory ? `Products - ${activeProductCategory.name}` : 'Products'}
      >
        <div className="products-add-row">
          <Input.TextArea
            rows={3}
            value={productInput}
            onChange={(event) => setProductInput(event.target.value)}
            placeholder="100001,100002,100003"
          />
          <Text type="secondary">
            Enter keycodes/products separated by commas, spaces, or new lines.
          </Text>
          <div className="products-actions-row">
            <div className="products-bulk-actions">
             
              <Button
                danger
                className="product-delete-button"
                icon={<DeleteOutlined />}
                disabled={selectedProductCodes.length === 0}
                loading={deletingSelectedProducts}
                onClick={handleDeleteSelectedProductCodes}
              >
                Delete
              </Button>
            </div>
            <Button type="primary" onClick={handleSaveProducts}>
              Add Keycodes
            </Button>
          </div>
        </div>

        <Table
          size="small"
          pagination={{
            current: productsCurrentPage,
            pageSize: productsPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100'],
            showTotal: (total) => `${total} products`,
            onChange: (page, nextPageSize) => {
              setProductsCurrentPage(page)
              setProductsPageSize(nextPageSize)
            }
          }}
          scroll={{ y: 280 }}
          rowKey="keycode"
          loading={productsLoading}
          locale={{ emptyText: 'No products saved yet.' }}
          dataSource={activeCategoryProducts.map((code) => ({ keycode: code, description: productDescriptions[code] }))}
          rowSelection={{
            selectedRowKeys: selectedProductCodes,
            onChange: setSelectedProductCodes
          }}
          columns={[
            {
              title: 'Keycode',
              dataIndex: 'keycode',
              key: 'keycode',
              width: 140
            },
            {
              title: 'Product Description',
              dataIndex: 'description',
              key: 'description',
              ellipsis: { showTitle: false },
              render: (description) => (description ? (
                <Tooltip title={description} placement="topLeft">
                  <span>{description}</span>
                </Tooltip>
              ) : <Text type="secondary">-</Text>)
            },
            {
              title: 'URL',
              key: 'url',
              width: 70,
              render: (_, record) => (
                <Tooltip title="Open on anko.com">
                  <a
                    href={buildProductUrl(record.keycode, record.description)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${record.keycode} on anko.com`}
                  >
                    <ExportOutlined />
                  </a>
                </Tooltip>
              )
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 110,
              render: (_, record) => (
                <Button
                  type="text"
                  danger
                  className="product-delete-button"
                  icon={<DeleteOutlined />}
                  onClick={() => confirmDeleteProductCode(record.keycode)}
                />
              )
            }
          ]}
        />
      </Modal>
    </div>
  )
}
export default ContentManagement
